import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconClock } from '@tabler/icons-react';
import { useDropdownDismiss } from './useDropdownDismiss';
import './TimeField.css';

/**
 * Custom time picker (C2). Replaces native <input type="time"> app-wide. The
 * value crosses the boundary as a 24-hour `HH:MM` string (what every existing
 * time input uses), displayed as 12-hour `hh:mm AM`. The trigger keeps the
 * floating label + border treatment; the picker portals to <body> and dismisses
 * on outside-click / Escape via the shared dropdown hook.
 *
 * Three scroll columns — hours (1–12), minutes (00–59), AM/PM. A draft of the
 * selection is held while the picker is open; "Done" commits it back as `HH:MM`,
 * "Clear" wipes the value. Both close the picker.
 */
interface TimeFieldProps {
  label: string;
  /** 24-hour `HH:MM` (or null/empty). */
  value: string | null;
  onChange: (hhmm: string | null) => void;
  disabled?: boolean;
  /** Red border + red helper text (validation display; logic stays in the host). */
  error?: boolean;
  /** Small caption shown beneath the field. */
  helperText?: string;
}

type Period = 'AM' | 'PM';

interface TimeParts {
  hour12: number; // 1–12
  minute: number; // 0–59
  period: Period;
}

const HOURS = Array.from({ length: 12 }, (_, i) => i + 1); // 1–12
const MINUTES = Array.from({ length: 60 }, (_, i) => i); // 0–59
const PERIODS: Period[] = ['AM', 'PM'];

const pad = (n: number) => String(n).padStart(2, '0');

/** Default draft when the field has no value yet — 9:00 AM. */
const DEFAULT_PARTS: TimeParts = { hour12: 9, minute: 0, period: 'AM' };

/** Parse a 24-hour `HH:MM` string into 12-hour parts. */
const parseValue = (value: string | null): TimeParts | null => {
  if (!value) return null;
  const m = /^(\d{1,2}):(\d{2})/.exec(value);
  if (!m) return null;
  const h24 = Number(m[1]);
  const minute = Number(m[2]);
  if (h24 < 0 || h24 > 23 || minute < 0 || minute > 59) return null;
  const period: Period = h24 < 12 ? 'AM' : 'PM';
  const hour12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return { hour12, minute, period };
};

/** Convert 12-hour parts back to a 24-hour `HH:MM` string. */
const toHHMM = ({ hour12, minute, period }: TimeParts): string => {
  let h24 = hour12 % 12; // 12 → 0
  if (period === 'PM') h24 += 12;
  return `${pad(h24)}:${pad(minute)}`;
};

const formatDisplay = ({ hour12, minute, period }: TimeParts): string =>
  `${pad(hour12)}:${pad(minute)} ${period}`;

export const TimeField: React.FC<TimeFieldProps> = ({
  label,
  value,
  onChange,
  disabled,
  error,
  helperText,
}) => {
  const [open, setOpen] = useState(false);
  const selected = parseValue(value);
  const [draft, setDraft] = useState<TimeParts>(selected ?? DEFAULT_PARTS);
  const triggerRef = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<React.CSSProperties>({
    position: 'fixed',
    visibility: 'hidden',
  });

  // Per-column refs so the selected row can be scrolled into view on open.
  const hourColRef = useRef<HTMLDivElement>(null);
  const minuteColRef = useRef<HTMLDivElement>(null);

  const close = useCallback(() => setOpen(false), []);
  useDropdownDismiss(pickerRef, triggerRef, close, open);

  const openPicker = () => {
    if (disabled) return;
    setDraft(selected ?? DEFAULT_PARTS);
    setOpen(true);
  };

  // Portalled: position below the trigger, flipping above when space is tight.
  useLayoutEffect(() => {
    if (!open) return;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const openAbove = window.innerHeight - rect.bottom < 300;
    setPosition(
      openAbove
        ? { position: 'fixed', bottom: window.innerHeight - rect.top + 4, left: rect.left }
        : { position: 'fixed', top: rect.bottom + 4, left: rect.left },
    );
  }, [open]);

  // Scroll the selected hour/minute rows into view once the picker is open
  // (after positioning, so the columns have a height to scroll within).
  useEffect(() => {
    if (!open) return;
    hourColRef.current
      ?.querySelector('.time-picker__item--selected')
      ?.scrollIntoView({ block: 'center' });
    minuteColRef.current
      ?.querySelector('.time-picker__item--selected')
      ?.scrollIntoView({ block: 'center' });
  }, [open]);

  const handleDone = () => {
    onChange(toHHMM(draft));
    setOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setOpen(false);
  };

  return (
    <div className="time-field" ref={triggerRef}>
      <span className="time-field__label">{label}</span>
      <div
        className={[
          'time-field__trigger',
          error ? 'time-field__trigger--error' : '',
          disabled ? 'time-field__trigger--disabled' : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={openPicker}
      >
        <span className={selected ? 'time-field__value' : 'time-field__placeholder'}>
          {selected ? formatDisplay(selected) : 'hh:mm'}
        </span>
        <button
          type="button"
          className="time-field__icon-btn"
          aria-label="Open time picker"
          tabIndex={-1}
          disabled={disabled}
          onClick={(e) => {
            e.stopPropagation();
            if (open) setOpen(false);
            else openPicker();
          }}
        >
          <IconClock size={13} />
        </button>
      </div>

      {helperText && (
        <div className={`time-field__helper ${error ? 'time-field__helper--error' : ''}`}>
          {helperText}
        </div>
      )}

      {open &&
        createPortal(
          <div className="time-picker" ref={pickerRef} style={position}>
            <div className="time-picker__header">
              <span className="time-picker__selected-display">
                {pad(draft.hour12)}:{pad(draft.minute)} {draft.period}
              </span>
            </div>

            <div className="time-picker__columns">
              <div className="time-picker__col" ref={hourColRef}>
                <div className="time-picker__col-label">Hr</div>
                {HOURS.map((h) => (
                  <div
                    key={h}
                    className={`time-picker__item ${
                      h === draft.hour12 ? 'time-picker__item--selected' : ''
                    }`}
                    onClick={() => setDraft((d) => ({ ...d, hour12: h }))}
                  >
                    {pad(h)}
                  </div>
                ))}
              </div>

              <div className="time-picker__col" ref={minuteColRef}>
                <div className="time-picker__col-label">Min</div>
                {MINUTES.map((m) => (
                  <div
                    key={m}
                    className={`time-picker__item ${
                      m === draft.minute ? 'time-picker__item--selected' : ''
                    }`}
                    onClick={() => setDraft((d) => ({ ...d, minute: m }))}
                  >
                    {pad(m)}
                  </div>
                ))}
              </div>

              <div className="time-picker__col">
                <div className="time-picker__col-label">AM/PM</div>
                {PERIODS.map((p) => (
                  <div
                    key={p}
                    className={`time-picker__item ${
                      p === draft.period ? 'time-picker__item--selected' : ''
                    }`}
                    onClick={() => setDraft((d) => ({ ...d, period: p }))}
                  >
                    {p}
                  </div>
                ))}
              </div>
            </div>

            <div className="time-picker__footer">
              <button type="button" className="time-picker__clear-btn" onClick={handleClear}>
                Clear
              </button>
              <button type="button" className="time-picker__done-btn" onClick={handleDone}>
                Done
              </button>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};
